/**
 * AgentRunPanel — Helpdesk Technician Agent UI (Admin/Owner only).
 *
 * Pilot constraints:
 *   - Read-only on PRODIGY-TEST-PC only
 *   - Feature flag must be enabled server-side
 *   - Admin/Owner access enforced server-side
 *   - No real customer messaging or remote-control actions (modelled state only)
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { agentRunsApi, agentsApi, customerProfilesApi, ticketsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { REJECT_QUICK_REASONS as _REJECT_QUICK_REASONS } from "../utils/agentRunConstants";
import { agentsForTicket, usableAgentId } from "../utils/agentScope";
import KnowledgeGatePanel from "./KnowledgeGatePanel";
import { degradedAnalysisLabel } from "./degradedAnalysisVocabulary";
import { actionTypeLabel } from "./actionTypeVocabulary";
import { protectionStatusMeta } from "./esetProtectionVocabulary";
import { runStatusLabel } from "./runStatusVocabulary";
import { complexityLabel, complexityTone, COMPLEXITY_TONE_UNKNOWN } from "./complexityVocabulary";
import { taskTypeLabel, TASK_TYPE_OPTIONS } from "./taskTypeVocabulary";
import { useReconnectRefetch } from "./useLiveState";

// ─── Status catalogue ─────────────────────────────────────────────────────────
// The wording for `run.status` lives in one place now (runStatusVocabulary), shared with the other
// screens that print the same field, and its fallback names an unrecognised status instead of
// echoing the producer's identifier.

// Statuses that require periodic polling for updates
const ACTIVE_STATUSES = new Set([
  "pending", "analyzing", "diagnosing", "connection_active", "executing",
]);

// Poll failures in a row (3s apart) before the panel stops tracking the active run
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

// Statuses where the technician must take action
const REVIEW_STATUSES = new Set([
  "awaiting_technician_review", "awaiting_approval",
]);

// Statuses where the run is paused and can be resumed
const PAUSED_STATUS = "paused";

// All statuses that prevent starting a new run (backend returns 409 for these)
const BLOCKING_STATUSES = new Set([
  ...ACTIVE_STATUSES,
  ...REVIEW_STATUSES,
  "waiting_for_customer_connection",
  "waiting_for_customer_answer",
  "paused",
]);

const OUTCOME_COLORS = {
  proposed:  "text-gray-500",
  approved:  "text-green-600",
  rejected:  "text-red-500",
  succeeded: "text-green-600",
  failed:    "text-red-500",
};

const OUTCOME_LABELS = {
  proposed:  "מוצע",
  approved:  "אושר",
  rejected:  "נדחה",
  succeeded: "הצליח",
  failed:    "נכשל",
};

const RISK_BADGE = {
  green: "bg-green-100 text-green-700 border-green-200",
  amber: "bg-yellow-100 text-yellow-700 border-yellow-200",
  red:   "bg-red-100 text-red-700 border-red-200",
};

const RISK_LABELS    = { green: "נמוך", amber: "בינוני", red: "גבוה" };

// The badge's colours stay this file's (the tone only says which arm was read). A value outside the
// scale gets a neutral chip: falling through to the last arm drew an unknown complexity as the
// simplest one, which is a claim the data does not make.
const COMPLEXITY_CHIP = {
  low:     "bg-green-50 text-green-700 border-green-200",
  medium:  "bg-amber-50 text-amber-700 border-amber-200",
  high:    "bg-red-50 text-red-700 border-red-200",
  [COMPLEXITY_TONE_UNKNOWN]: "bg-gray-100 text-gray-600 border-gray-200",
};

function formatExpiry(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return iso; }
}

// ─── CopyLinkButton ───────────────────────────────────────────────────────────

function CopyLinkButton({ link }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* graceful failure */ }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid="copy-customer-link"
      className="flex items-center gap-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300 px-3 py-1.5 rounded text-xs font-medium transition-colors"
    >
      {copied ? "✓ הועתק!" : "העתק קישור ללקוח"}
    </button>
  );
}

function CopyButton({ text, label = "העתק" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* graceful failure */ }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-300 px-2 py-1 rounded transition-colors"
    >
      {copied ? "✓ הועתק" : label}
    </button>
  );
}

function confidenceColor(n) {
  if (n >= 70) return "bg-blue-500";
  if (n >= 50) return "bg-yellow-400";
  return "bg-red-400";
}

function cardClass(status) {
  if (status === "completed" || status === "resolved") return "border-green-200 bg-green-50/40";
  if (status === "failed" || status === "rejected")   return "border-red-200 bg-red-50/40";
  if (REVIEW_STATUSES.has(status))                    return "border-amber-200 bg-amber-50/40";
  if (ACTIVE_STATUSES.has(status))                    return "border-violet-200 bg-violet-50/40";
  if (status === "waiting_for_customer_connection")   return "border-blue-200 bg-blue-50/40";
  if (status === PAUSED_STATUS)                       return "border-gray-300 bg-gray-100/60";
  return "border-gray-200 bg-white";
}

function statusBadgeClass(status) {
  if (status === "completed" || status === "resolved") return "bg-green-100 text-green-700";
  if (status === "failed" || status === "rejected")   return "bg-red-100 text-red-700";
  if (REVIEW_STATUSES.has(status))                    return "bg-amber-100 text-amber-700";
  if (ACTIVE_STATUSES.has(status))                    return "bg-violet-100 text-violet-700";
  if (status === "waiting_for_customer_connection")   return "bg-blue-100 text-blue-700";
  if (status === PAUSED_STATUS)                       return "bg-gray-200 text-gray-600";
  return "bg-gray-100 text-gray-600";
}

// ─── AnalysisDisplay ──────────────────────────────────────────────────────────

/**
 * The compose behind `AnalysisDisplay`'s `שאל לקוח` button: the analysis's own `missing_information`,
 * numbered into a mail the technician can put in the customer email form.
 *
 * `missing_information` is a field the product already refuses on a fallback arm —
 * `knowledge_gap_service.extract_gaps_from_run` returns no gaps at all for a degraded analysis, and
 * `EmployeeWorkspace.learningCandidates` (FULL-0040) withholds the same field on the same arm — because
 * the mock the pilot default builds carries the same standing sentences on every run, and they are notes
 * about the analysis rather than questions for the customer. This control is the one reader of that field
 * whose output leaves the screen addressed to the customer, so it refuses on the same arm.
 *
 * Returns `{ draft, withheldLabel }`. Only one is ever non-null. `withheldLabel` is non-null only when
 * there was something to withhold, so a fallback analysis with no entries renders exactly as before.
 */
export function customerQuestionDraft(analysis) {
  const items = Array.isArray(analysis?.missing_information) ? analysis.missing_information : [];
  if (items.length === 0) return { draft: null, withheldLabel: null };
  const withheldLabel = degradedAnalysisLabel(analysis?.degraded);
  if (withheldLabel) return { draft: null, withheldLabel };
  const numbered = items.map((q, i) => `${i + 1}. ${q}`).join("\n");
  return {
    draft: `שלום,\n\nלצורך המשך טיפול בפנייתך, נדרש מידע נוסף:\n\n${numbered}\n\nתודה על שיתוף הפעולה.`,
    withheldLabel: null,
  };
}

function AnalysisDisplay({ analysis, onFillReply }) {
  if (!analysis) return null;
  const fih = analysis.fact_inference_hypothesis || {};
  // The pilot default builds the deterministic mock when no ANALYSIS_PROVIDER is configured, and
  // marks it degraded: its prose is the fallback adapter's own scaffolding, not a finding about
  // this ticket. Read from the shared vocabulary so no surface words one arm two ways.
  const degradedLabel = degradedAnalysisLabel(analysis.degraded);
  // The customer-question compose is offered only when the run recorded something it may be built from,
  // and on the fallback arm the answer is the shared label rather than a mail to the customer.
  const questionOffer = customerQuestionDraft(analysis);

  return (
    <div className="space-y-3 text-sm">
      {/* Prefixed so it is read before the words it qualifies — the confidence meter, the finding,
          the hypotheses and the recommended step below all belong to this same analysis. */}
      {degradedLabel && (
        <p
          data-testid="analysis-degraded"
          className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
        >
          {degradedLabel}
        </p>
      )}

      {/* Confidence */}
      <div>
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>ביטחון AI</span>
          <span className="font-medium">{analysis.confidence ?? "—"}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${confidenceColor(analysis.confidence ?? 0)}`}
            style={{ width: `${analysis.confidence ?? 0}%` }}
          />
        </div>
        {analysis.confidence < 40 ? (
          <div className="mt-1.5 rounded-md bg-red-50 border border-red-200 px-2.5 py-1.5 text-xs text-red-700">
            <span className="font-semibold">ביטחון נמוך מאוד</span> — הניתוח ספקולטיבי. מומלץ לאסוף מידע נוסף מהלקוח לפני נקיטת פעולה כלשהי.
            {analysis.missing_information?.length > 0 && (
              <span className="block mt-0.5 text-red-600">ראה "מידע חסר" לשאלות ממוקדות.</span>
            )}
          </div>
        ) : analysis.confidence < 60 ? (
          <p className="text-xs text-amber-600 mt-1">
            ביטחון נמוך — נדרשות ראיות אבחוניות נוספות לפני פעולה
          </p>
        ) : null}
      </div>

      {/* Likely issue */}
      {analysis.likely_issue && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            בעיה אפשרית
          </p>
          <p className="text-gray-700">{analysis.likely_issue}</p>
        </div>
      )}

      {/* Metadata badges: risk, task type, complexity */}
      {(analysis.risk_level || analysis.task_type || analysis.complexity) && (
        <div className="flex flex-wrap items-center gap-2">
          {analysis.risk_level && (
            <span className={`text-xs px-2 py-0.5 rounded border font-medium ${RISK_BADGE[analysis.risk_level] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
              סיכון: {RISK_LABELS[analysis.risk_level] ?? analysis.risk_level}
            </span>
          )}
          {analysis.task_type && (
            <span className="text-xs px-2 py-0.5 rounded border font-medium bg-blue-50 text-blue-700 border-blue-200">
              {taskTypeLabel(analysis.task_type)}
            </span>
          )}
          {analysis.complexity && (
            <span className={`text-xs px-2 py-0.5 rounded border font-medium ${
              COMPLEXITY_CHIP[complexityTone(analysis.complexity)]
            }`}>
              {complexityLabel(analysis.complexity)}
            </span>
          )}
        </div>
      )}

      {/* Missing information */}
      {analysis.missing_information?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">מידע חסר</p>
            {onFillReply && !analysis.customer_reply_draft && questionOffer.draft && (
              <button
                type="button"
                onClick={() => onFillReply(questionOffer.draft)}
                className="text-xs text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 px-2 py-0.5 rounded transition-colors"
                title="מלא טופס מייל עם השאלות הנדרשות"
              >
                שאל לקוח ↗
              </button>
            )}
            {/* Withheld rather than silently absent: the expected control is missing here on the pilot
                default, so the reader is told why instead of having to infer it from the pill above. */}
            {onFillReply && !analysis.customer_reply_draft && questionOffer.withheldLabel && (
              <span
                data-testid="customer-question-withheld"
                className="text-xs font-medium text-amber-800"
              >
                שאילת לקוח לא הוצעה — {questionOffer.withheldLabel}
              </span>
            )}
          </div>
          <ul className="list-disc list-inside space-y-0.5 text-gray-600">
            {analysis.missing_information.map((item, i) => (
              <li key={i} className="text-xs">{item}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Alternative hypotheses */}
      {analysis.alternative_hypotheses?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            השערות נוספות
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-gray-600">
            {analysis.alternative_hypotheses.map((h, i) => (
              <li key={i} className="text-xs">{h}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommended next step */}
      {analysis.recommended_next_step && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            הצעד הבא
          </p>
          <p className="text-gray-700 text-xs">{analysis.recommended_next_step}</p>
        </div>
      )}

      {/* First diagnostic to run */}
      {analysis.recommended_next_diagnostic && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            אבחון ראשון לביצוע
          </p>
          <p className="text-gray-700 text-xs">{analysis.recommended_next_diagnostic}</p>
        </div>
      )}

      {/* Proposed diagnostics */}
      {analysis.proposed_diagnostics?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            אבחונים מומלצים (Read-only)
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-gray-600">
            {analysis.proposed_diagnostics.map((d, i) => (
              <li key={i} className="text-xs">{d}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Proposed action (Phase 2 groundwork) */}
      {analysis.proposed_action && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">פעולה מוצעת</span>
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">בטוח</span>
          </div>
          <p className="text-sm text-gray-800">{analysis.proposed_action.description}</p>
          {analysis.proposed_action.target && (
            <p className="text-xs text-gray-500 mt-0.5">יעד: {analysis.proposed_action.target}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">סוג: {actionTypeLabel(analysis.proposed_action.action_type)}</p>
        </div>
      )}

      {/* ── Customer-facing section separator ────────────────────────────── */}
      {analysis.customer_reply_draft && (
        <div className="pt-2 border-t-2 border-dashed border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-amber-600 uppercase tracking-wide">
              ✉ תגובה מוצעת ללקוח — לפני שליחה נדרשת סקירת טכנאי
            </p>
            <div className="flex gap-1.5">
              {onFillReply && (
                <button
                  type="button"
                  onClick={() => onFillReply(analysis.customer_reply_draft)}
                  className="text-xs text-violet-700 bg-violet-100 hover:bg-violet-200 border border-violet-300 px-2 py-1 rounded transition-colors"
                >
                  מלא טופס מייל ↗
                </button>
              )}
              <CopyButton text={analysis.customer_reply_draft} />
            </div>
          </div>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
              {analysis.customer_reply_draft}
            </p>
          </div>
          <p className="text-xs text-amber-500 mt-1">
            זוהי טיוטה בלבד. לא נשלחה ללקוח. נדרש אישור מפורש לפני כל שליחה.
          </p>
        </div>
      )}

      {/* Connection required */}
      {analysis.connection_required && (
        <div className="flex items-center gap-1.5 text-xs text-blue-600">
          <span>⚡</span>
          <span>נדרש חיבור מרחוק לביצוע האבחון</span>
        </div>
      )}

      {/* FACT / INFERENCE / HYPOTHESIS */}
      {(fih.facts?.length > 0 || fih.inferences?.length > 0 || fih.hypotheses?.length > 0) && (
        <details className="group">
          <summary className="text-xs text-gray-400 cursor-pointer select-none hover:text-gray-600">
            עובדות / הסקות / השערות
          </summary>
          <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-gray-200">
            {fih.facts?.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-green-700">עובדות: </span>
                {fih.facts.map((f, i) => <span key={i} className="text-xs text-gray-600 block pl-2">{f}</span>)}
              </div>
            )}
            {fih.inferences?.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-yellow-600">הסקות: </span>
                {fih.inferences.map((f, i) => <span key={i} className="text-xs text-gray-600 block pl-2">{f}</span>)}
              </div>
            )}
            {fih.hypotheses?.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500">השערות: </span>
                {fih.hypotheses.map((f, i) => <span key={i} className="text-xs text-gray-600 block pl-2">{f}</span>)}
              </div>
            )}
          </div>
        </details>
      )}

      {/* KB article shortcut — shown when confidence is low and a likely issue is identified */}
      {analysis.confidence < 60 && analysis.likely_issue && (
        <div className="pt-2 border-t border-dashed border-gray-200">
          <Link
            to={`/knowledge?keyword=${encodeURIComponent(analysis.likely_issue.slice(0, 120))}`}
            className="inline-flex items-center gap-1 text-xs text-violet-600 hover:text-violet-800 hover:underline"
            title="פתח את מאגר הידע עם טופס יצירת מאמר ממולא מראש לפי הבעיה שזוהתה"
          >
            📚 צור מאמר ידע לשיפור דיוק AI בנושא זה
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── EditApproveForm — inline edit before approval ───────────────────────────

const _ACTION_DESC_LABEL = {
  add_comment:           "תוכן ההערה הפנימית",
  close_ticket:          "הודעת סגירה",
  escalate:              "סיבת הסלמה",
};
const _EDITABLE_DESC_TYPES = new Set(["add_comment", "close_ticket", "escalate"]);
const _EDIT_QUICK_REASONS = ["אבחון שגוי", "פעולה לא מתאימה", "ניסוח לא מדויק", "חסר הקשר", "אחר"];

function EditApproveForm({ run, onSubmit, onCancel, busy }) {
  const a = run.analysis || {};
  const proposedType = a.proposed_action?.action_type;
  const showDescEditor = proposedType && _EDITABLE_DESC_TYPES.has(proposedType);
  const [likelyIssue, setLikelyIssue]     = useState(a.likely_issue || "");
  const [nextStep, setNextStep]           = useState(a.recommended_next_step || "");
  const [replyDraft, setReplyDraft]       = useState(a.customer_reply_draft || "");
  const [actionDesc, setActionDesc]       = useState(a.proposed_action?.description || "");
  const [taskType, setTaskType]           = useState(a.task_type || "");
  const [reason, setReason]               = useState("");

  const handleSubmit = () => {
    const edited = {
      ...run.analysis,
      likely_issue: likelyIssue,
      recommended_next_step: nextStep,
      customer_reply_draft: replyDraft || null,
      task_type: taskType || a.task_type || null,
    };
    if (showDescEditor && a.proposed_action) {
      edited.proposed_action = { ...a.proposed_action, description: actionDesc };
    }
    const analysisChanged = likelyIssue !== (a.likely_issue || "") || nextStep !== (a.recommended_next_step || "") || taskType !== (a.task_type || "");
    const replyChanged    = replyDraft !== (a.customer_reply_draft || "");
    const actionChanged   = showDescEditor && actionDesc !== (a.proposed_action?.description || "");
    let editType = "analysis_edit";
    if (!analysisChanged && actionChanged) editType = "action_edit";
    else if (!analysisChanged && replyChanged) editType = "reply_edit";
    onSubmit({
      edit_type: editType,
      original_ai_output: run.analysis,
      technician_edited_output: edited,
      reason: reason.trim() || null,
    });
  };

  return (
    <div className="mt-3 space-y-3 border-t border-amber-200 pt-3">
      <p className="text-xs font-semibold text-amber-700">ערוך ניתוח לפני אישור</p>

      <label className="block">
        <span className="text-xs text-gray-500">בעיה אפשרית</span>
        <textarea
          value={likelyIssue}
          onChange={(e) => setLikelyIssue(e.target.value)}
          rows={2}
          className="mt-0.5 block w-full rounded border border-gray-300 text-sm px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
      </label>

      {a.task_type && (
        <div>
          <span className="text-xs text-gray-500 block mb-1">קטגוריית משימה</span>
          <div className="flex flex-wrap gap-1.5">
            {TASK_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTaskType(opt.value)}
                className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${
                  taskType === opt.value
                    ? "bg-blue-100 border-blue-400 text-blue-700 font-medium"
                    : "bg-gray-50 border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="block">
        <span className="text-xs text-gray-500">הצעד הבא המומלץ</span>
        <textarea
          value={nextStep}
          onChange={(e) => setNextStep(e.target.value)}
          rows={2}
          className="mt-0.5 block w-full rounded border border-gray-300 text-sm px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
      </label>

      {showDescEditor && (
        <label className="block">
          <span className="text-xs text-gray-500">
            {_ACTION_DESC_LABEL[proposedType] ?? "תיאור הפעולה"}
          </span>
          <textarea
            value={actionDesc}
            onChange={(e) => setActionDesc(e.target.value)}
            dir="auto"
            rows={proposedType === "add_comment" ? 5 : 3}
            className="mt-0.5 block w-full rounded border border-gray-300 text-sm px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
        </label>
      )}

      <label className="block">
        <span className={`text-xs ${proposedType === "close_ticket" || proposedType === "send_customer_message" ? "text-blue-600 font-medium" : "text-gray-500"}`}>
          {proposedType === "close_ticket"
            ? "✉ מייל סגירה ללקוח — יישלח אוטומטית עם האישור"
            : proposedType === "send_customer_message"
            ? "✉ טיוטת תגובה ללקוח — נדרש תוכן כדי שהפעולה תבוצע"
            : "טיוטת תגובה ללקוח (אופציונלי)"}
        </span>
        <textarea
          value={replyDraft}
          onChange={(e) => setReplyDraft(e.target.value)}
          rows={proposedType === "close_ticket" ? 3 : 2}
          className="mt-0.5 block w-full rounded border border-gray-300 text-sm px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
        {proposedType === "send_customer_message" && !replyDraft.trim() && (
          <p className="text-xs text-red-600 mt-1">השדה ריק — אישור עם תוכן ריק לא ישלח דבר ללקוח</p>
        )}
      </label>

      {/* Reason for edit — learning signal */}
      <div className="space-y-1.5 border-t border-amber-100 pt-2">
        <p className="text-[11px] text-amber-600 font-medium">סיבת התיקון (מומלץ — משפר את למידת הסוכן)</p>
        <div className="flex flex-wrap gap-1.5">
          {_EDIT_QUICK_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(reason === r ? "" : r)}
              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                reason === r
                  ? "bg-amber-100 border-amber-400 text-amber-700 font-medium"
                  : "bg-gray-50 border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={1}
          placeholder="פרט מה תוקן ומדוע..."
          className="block w-full rounded border border-gray-200 text-xs px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-amber-300 text-gray-600 placeholder-gray-300"
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy || (proposedType === "send_customer_message" && !replyDraft.trim())}
          title={proposedType === "send_customer_message" && !replyDraft.trim() ? "יש להזין תוכן לתגובה ללקוח" : undefined}
          className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
        >
          {busy ? "שולח..." : "שמור ואשר"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-xs font-medium transition-colors"
        >
          ביטול
        </button>
      </div>
    </div>
  );
}

// ─── RejectForm ───────────────────────────────────────────────────────────────

function RejectForm({ onSubmit, onCancel, busy }) {
  const [reason, setReason] = useState("");
  return (
    <div className="mt-3 space-y-2 border-t border-red-200 pt-3">
      <p className="text-xs font-semibold text-red-700">סיבת דחייה (אופציונלי)</p>
      <div className="flex flex-wrap gap-1.5">
        {_REJECT_QUICK_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setReason(r)}
            className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
              reason === r
                ? "bg-red-100 border-red-400 text-red-700 font-medium"
                : "bg-gray-50 border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600"
            }`}
          >
            {r}
          </button>
        ))}
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="תאר מדוע הניתוח נדחה..."
        className="block w-full rounded border border-gray-300 text-sm px-2 py-1 resize-none focus:outline-none focus:ring-1 focus:ring-red-400"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSubmit(reason)}
          disabled={busy}
          className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
        >
          {busy ? "שולח..." : "אשר דחייה"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-xs font-medium"
        >
          ביטול
        </button>
      </div>
    </div>
  );
}

// ─── RunCard ──────────────────────────────────────────────────────────────────

function RunCard({ run, onRunUpdated, onNewRun, onFillReply, isLatest }) {
  const _isTerminal = !ACTIVE_STATUSES.has(run.status) && !REVIEW_STATUSES.has(run.status) && !BLOCKING_STATUSES.has(run.status);
  const [collapsed, setCollapsed]             = useState(_isTerminal && !isLatest);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [editMode, setEditMode]               = useState(false);
  const [rejectMode, setRejectMode]           = useState(false);
  const [busy, setBusy]                       = useState(false);
  const [localError, setLocalError]           = useState(null);
  const [gateVisible, setGateVisible]         = useState(false);
  const [rating, setRating]                   = useState(null); // "thumbs_up" | "thumbs_down"
  const [ratingBusy, setRatingBusy]           = useState(false);

  const { can: canAgent } = useAgentPermissions();

  const { data: _runFeedback = [] } = useQuery({
    queryKey: ["agentRunFeedback", run.id],
    queryFn: () => agentRunsApi.feedbackHistory(run.id),
    enabled: _isTerminal && !rating,
    staleTime: 60000,
  });
  useEffect(() => {
    if (!_runFeedback.length || rating) return;
    const existing = _runFeedback.find((f) => f.edit_type === "quality_rating");
    if (existing) setRating(existing.technician_edited_output?.rating ?? null);
  }, [_runFeedback, rating]);

  const isActive    = ACTIVE_STATUSES.has(run.status);
  const isReview    = REVIEW_STATUSES.has(run.status);
  const isWaitConn  = run.status === "waiting_for_customer_connection";
  const isConnActive = run.status === "connection_active";
  const isPaused    = run.status === PAUSED_STATUS;
  const isFailed    = run.status === "failed";

  const handleAction = async (apiFn, ...args) => {
    setBusy(true);
    setLocalError(null);
    try {
      const updated = await apiFn(run.id, ...args);
      onRunUpdated(updated);
      return true;
    } catch (err) {
      setLocalError(err?.response?.data?.detail || "שגיאה בביצוע הפעולה");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // An approve can be refused for reasons the card cannot see. The one a technician can act on is the
  // Adaptive Knowledge Gate: the backend stops the approved mutation and answers with a single line of
  // Hebrew, and the evidence that clears it is recorded elsewhere. So a refused approve reveals the
  // gate surface instead of leaving a dead end. It is truthful when the gate is not the cause — it
  // says "no mismatch was ever recorded against this run" — so this never invents a block.
  const handleApprove = async () => {
    if (!(await handleAction(agentRunsApi.approve))) setGateVisible(true);
  };
  const handleStop            = () => handleAction(agentRunsApi.stop);
  const handleResume          = () => handleAction(agentRunsApi.resume);
  const handleConnectionActive = () => handleAction(agentRunsApi.connectionActive);
  const handleRate = async (value) => {
    setRatingBusy(true);
    setLocalError(null);
    try {
      await agentRunsApi.rate(run.id, value);
      setRating(value);
    } catch (err) {
      // was swallowed: a refused rating looked like a dead button
      setLocalError(err?.response?.data?.detail || "שגיאה בשמירת הדירוג");
    } finally {
      setRatingBusy(false);
    }
  };
  const handleRetry = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      const newRun = await agentRunsApi.retry(run.id);
      onNewRun(newRun);
    } catch (err) {
      setLocalError(err?.response?.data?.detail || "שגיאה בניסיון חוזר");
    } finally {
      setBusy(false);
    }
  };
  // The forms close only when the request SUCCEEDED: closing first threw away the technician's edited reply / reason
  // whenever the API refused (e.g. 409 for approving a run you started yourself).
  const handleFeedback = async (payload) => {
    if (await handleAction(agentRunsApi.feedback, payload)) setEditMode(false);
  };
  const handleReject = async (reason) => {
    if (await handleAction(agentRunsApi.reject, reason)) setRejectMode(false);
  };

  // "request more analysis" = start a fresh run preserving the same agent
  const handleRequestMore = async () => {
    setBusy(true);
    setLocalError(null);
    try {
      const newRun = await agentRunsApi.start(run.ticket_id, run.agent_id || undefined);
      onNewRun(newRun);
    } catch (err) {
      setLocalError(err?.response?.data?.detail || "שגיאה בהתחלת ניתוח חדש");
    } finally {
      setBusy(false);
    }
  };

  if (collapsed) {
    // The collapsed row prints the analysis's finding and nothing else of the analysis, so it needs
    // the same disclosure the expanded `AnalysisDisplay` carries — otherwise a fallback adapter's
    // scaffolding reads as the run's summary. Same shared vocabulary, same `analysis-degraded` testid:
    // one arm, one wording, whichever card renders it.
    const degradedLabel = degradedAnalysisLabel(run.analysis?.degraded);
    return (
      <div
        className={`rounded-lg border px-3 py-2 flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 ${cardClass(run.status)}`}
        onClick={() => setCollapsed(false)}
        title="לחץ להרחבה"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${statusBadgeClass(run.status)}`}>
            {runStatusLabel(run.status)}
          </span>
          {/* Before the finding it qualifies, so it is read first. `shrink-0` keeps the label intact
              and lets the scaffolding text truncate instead. */}
          {degradedLabel && (
            <span
              data-testid="analysis-degraded"
              className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 shrink-0"
            >
              {degradedLabel}
            </span>
          )}
          {run.analysis?.likely_issue && (
            <span className="text-xs text-gray-500 truncate">{run.analysis.likely_issue}</span>
          )}
        </div>
        <span className="text-xs text-gray-400 shrink-0">{new Date(run.created_at).toLocaleDateString("he-IL")} ▸</span>
      </div>
    );
  }

  // The expanded card prints the analysis's finding and — unlike the collapsed row — also offers to
  // fill the customer email form with the analysis's reply draft. That offer sits outside the
  // `<details>` that carries `AnalysisDisplay`'s disclosure, and on a card whose details are never
  // opened the only thing a technician reads before clicking "מלא טופס מייל" is the draft offer
  // itself. So the same arm needs the same label here, from the same shared vocabulary.
  const cardDegradedLabel = degradedAnalysisLabel(run.analysis?.degraded);

  return (
    <div className={`rounded-lg border p-4 space-y-3 ${cardClass(run.status)}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {isActive && (
            <div className="animate-spin h-4 w-4 border-2 border-violet-600 border-t-transparent rounded-full shrink-0" />
          )}
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusBadgeClass(run.status)}`}>
            {runStatusLabel(run.status)}
          </span>
          {isActive && (() => {
            const startMs = run.started_at ? new Date(run.started_at) : new Date(run.created_at);
            const elapsedMin = Math.floor((Date.now() - startMs) / 60000);
            if (elapsedMin < 1) return null;
            const stuck = elapsedMin >= 15;
            return (
              <span
                className={`text-xs px-1.5 py-0.5 rounded font-medium ${stuck ? "bg-amber-100 text-amber-700" : "text-gray-400"}`}
                title={stuck ? "הריצה ארוכה מהרגיל — ייתכן שתקועה" : "משך הניתוח הנוכחי"}
              >
                {elapsedMin}ד{stuck ? " ⚠" : ""}
              </span>
            );
          })()}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>{new Date(run.created_at).toLocaleString("he-IL")}</span>
          {run.duration_ms != null && (
            <span title="משך הניתוח">
              {run.duration_ms < 60000
                ? `${(run.duration_ms / 1000).toFixed(1)}ש`
                : `${Math.round(run.duration_ms / 60000)}ד`}
            </span>
          )}
          {run.estimated_cost != null && run.estimated_cost > 0 && (
            <span title="עלות AI מוערכת">
              ${run.estimated_cost < 0.01
                ? run.estimated_cost.toFixed(4)
                : run.estimated_cost.toFixed(3)}
            </span>
          )}
          <Link
            to={`/agent-runs/${run.id}`}
            className="text-brand-500 hover:underline shrink-0"
            title="פרטים מלאים"
          >
            פרטים →
          </Link>
        </div>
      </div>

      {/* Paused banner */}
      {isPaused && (
        <div className="rounded-md bg-gray-50 border border-gray-300 p-3 text-sm text-gray-600 space-y-2">
          <p className="font-medium">הסוכן הופסק</p>
          {run.context?.stop_reason && (
            <p className="text-xs text-gray-500">סיבה: {run.context.stop_reason}</p>
          )}
          {run.analysis && (
            <p className="text-xs text-gray-500">הניתוח הקיים שמור וישוחזר בהמשך.</p>
          )}
        </div>
      )}

      {/* Connection waiting banner — shows TeamViewer session card if available */}
      {isWaitConn && (() => {
        const sess = run.context?.attended_session;
        return (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700 space-y-2">
            <p className="font-medium">נדרש חיבור מרחוק</p>
            {sess ? (
              <>
                <div className="text-xs space-y-1 text-blue-800">
                  <p>מצב: <span className="font-medium">ממתין לחיבור הלקוח</span></p>
                  {sess.state && (
                    <p>TeamViewer: <span className="font-medium">{sess.state}</span></p>
                  )}
                  {sess.valid_until && (
                    <p>תוקף עד: <span className="font-medium">{formatExpiry(sess.valid_until)}</span></p>
                  )}
                </div>
                {sess.customer_link && (
                  <CopyLinkButton link={sess.customer_link} />
                )}
                <p className="text-xs text-blue-500">
                  שלח את הקישור ללקוח. לאחר שהלקוח התחבר, לחץ "סמן חיבור פעיל".
                </p>
              </>
            ) : (
              <p className="text-xs text-blue-500">
                ממתין לחיבור. לאחר ביסוס החיבור, לחץ "סמן חיבור פעיל".
              </p>
            )}
          </div>
        );
      })()}

      {/* Connection active — brief transient state before diagnostics start */}
      {isConnActive && (
        <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700 space-y-1">
          <p className="font-medium">חיבור פעיל — מוכן לאבחון</p>
          <p className="text-xs text-green-500">האבחון יתחיל אוטומטית.</p>
        </div>
      )}

      {/* ESET-POC-001: read-only device security observation */}
      {run.analysis?.eset_observation && (() => {
        const obs = run.analysis.eset_observation;
        const atRisk = obs.protection_status === "at_risk";
        const protection = protectionStatusMeta(obs.protection_status);
        return (
          <div className={`rounded-md border p-2 space-y-1 ${atRisk ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}>
            <div className="flex items-center justify-between flex-wrap gap-1">
              <p className="text-xs font-semibold text-gray-500">
                בדיקת אבטחת מכשיר — ESET{obs.device_identifier ? ` · ${obs.device_identifier}` : ""}
              </p>
              {obs.is_mock && (
                <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-amber-100 text-amber-700 border-amber-200">
                  MOCK — נתוני בדיקה בלבד
                </span>
              )}
            </div>
            <p className={`text-sm font-medium ${protection.cls}`} data-testid="eset-protection-status">
              {protection.label}
            </p>
            {obs.threats?.length > 0 && (
              <ul className="text-xs text-red-700 space-y-0.5">
                {obs.threats.map((t, i) => (
                  <li key={i}>• {t.name} ({t.severity}) — {t.action_taken} · {t.detected_utc}</li>
                ))}
              </ul>
            )}
            <p className="text-[10px] text-gray-400">
              מקור: {obs.source} · סנכרון אחרון: {obs.last_sync_utc ? new Date(obs.last_sync_utc).toLocaleString("he-IL") : "לא ידוע"}
              {" · "}נצפה: {obs.observed_utc ? new Date(obs.observed_utc).toLocaleString("he-IL") : "לא ידוע"}
            </p>
          </div>
        );
      })()}

      {/* Diagnostic synopsis (after connection phase) */}
      {run.analysis?.diagnostic_synopsis && (
        <div className="rounded-md bg-white border border-gray-200 p-2">
          <p className="text-xs font-semibold text-gray-500 mb-1">תוצאת אבחון</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {run.analysis.diagnostic_synopsis}
          </p>
        </div>
      )}

      {/* KB articles that influenced this analysis */}
      {run.context?.kb_articles_used?.length > 0 && (
        <div className="rounded-md border border-blue-100 bg-blue-50/60 px-2.5 py-2">
          <p className="text-xs font-semibold text-blue-600 mb-1">ידע שנוצל בניתוח</p>
          <ul className="space-y-0.5">
            {run.context.kb_articles_used.map((a) => (
              <li key={a.id} className="text-xs text-blue-700">
                <Link to={`/knowledge?search=${encodeURIComponent(a.title)}`} className="hover:underline">• {a.title}</Link>
                {(a.scope || a.updated_at) && (
                  <span className="mr-2 text-[10px] text-blue-500">
                    {a.scope === "customer" ? "לקוח" : "ארגון"}
                    {a.updated_at ? ` · עודכן ${new Date(a.updated_at).toLocaleDateString("he-IL")}` : ""}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Context signals injected into this analysis */}
      {(isReview || isActive) && run.context?.context_signals && (() => {
        const cs = run.context.context_signals;
        const chips = [];
        if (cs.contact_history) {
          const recurring = cs.contact_history.recurring_issue;
          chips.push({ label: recurring ? "בעיה חוזרת ⚠" : "היסטוריית לקוח", color: recurring ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-gray-100 text-gray-500 border-gray-200" });
        }
        if (cs.cluster_signal) chips.push({ label: `${cs.cluster_signal.ticket_count} קריאות פתוחות דומות ⚠`, color: "bg-amber-100 text-amber-700 border-amber-200" });
        if (cs.company_issue_patterns) chips.push({ label: "דפוסי חברה", color: "bg-gray-100 text-gray-500 border-gray-200" });
        if (cs.company_action_patterns) chips.push({ label: "היסטוריית פעולות", color: "bg-gray-100 text-gray-500 border-gray-200" });
        if (cs.knowledge_gaps?.count > 0) chips.push({ label: `${cs.knowledge_gaps.count} פערי ידע`, color: "bg-gray-100 text-gray-500 border-gray-200" });
        if (cs.rejection_context) chips.push({ label: "דחייה קודמת", color: "bg-red-50 text-red-600 border-red-200" });
        if (!chips.length && !(cs.cluster_signal?.ticket_numbers?.length)) return null;
        return (
          <div className="space-y-1">
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {chips.map((c, i) => (
                  <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${c.color}`}>{c.label}</span>
                ))}
              </div>
            )}
            {cs.cluster_signal?.ticket_numbers?.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5">
                <p className="text-[10px] font-semibold text-amber-700 mb-1">קריאות פתוחות קשורות:</p>
                <div className="flex flex-wrap gap-1">
                  {cs.cluster_signal.ticket_numbers.map((num) => (
                    <Link key={num} to={`/tickets?search=%23${num}&status=all`} className="text-[10px] text-amber-800 bg-white border border-amber-300 px-1.5 py-0.5 rounded hover:bg-amber-100 transition-colors">
                      #{num}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Similar ticket references (review state context) */}
      {isReview && run.context?.similar_ticket_references?.length > 0 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2">
          <p className="text-xs font-semibold text-gray-500 mb-1">קריאות דומות שנסגרו</p>
          <ul className="space-y-1.5">
            {run.context.similar_ticket_references.map((ref, i) => {
              const title = typeof ref === "string" ? ref : ref.title;
              const resolution = typeof ref === "string" ? null : ref.resolution_note;
              const ticketId = typeof ref === "object" ? ref.id : null;
              const ticketNum = typeof ref === "object" ? ref.ticket_number : null;
              return (
                <li key={i} className="text-xs text-gray-600">
                  {ticketId ? (
                    <Link to={`/tickets/${ticketId}`} className="hover:underline text-brand-600">
                      • {ticketNum ? `#${ticketNum} — ` : ""}{title}
                    </Link>
                  ) : (
                    <span>• {title}</span>
                  )}
                  {resolution && (
                    <span className="block mr-3 mt-0.5 text-green-700 bg-green-50 border border-green-100 rounded px-1.5 py-0.5">
                      פתרון: {resolution}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Structured analysis (review state) */}
      {isReview && run.analysis && (
        <AnalysisDisplay analysis={run.analysis} onFillReply={onFillReply} />
      )}

      {/* Result summary (terminal states) */}
      {run.result_summary && !isReview && (
        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
          {run.result_summary}
        </p>
      )}

      {/* Rejection reason */}
      {run.status === "rejected" && run.context?.reject_reason && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">
          סיבת דחייה: {run.context.reject_reason}
          {run.context.technician && (
            <span className="mr-1 text-red-400"> · נדחה על ידי {run.context.technician}</span>
          )}
        </p>
      )}

      {/* Who initiated the run */}
      {run.initiated_by && (
        <p className="text-xs text-gray-500">
          הופעל על ידי:{" "}
          {run.initiated_by === "auto:email_reply"
            ? "אוטומטי — תגובת לקוח"
            : run.initiated_by === "auto:new_ticket"
            ? "אוטומטי — קריאה חדשה"
            : run.initiated_by === "auto:scheduled_check"
            ? `אוטומטי — בדיקה מתוזמנת${run.context?.check_name ? `: ${run.context.check_name}` : ""}`
            : run.initiated_by}
        </p>
      )}
      {run.context?.system_check && run.context?.check_name && run.initiated_by !== "auto:scheduled_check" && (
        <p className="text-xs text-gray-500">בדיקה מתוזמנת: {run.context.check_name}</p>
      )}

      {/* Who approved (approved/completed/resolved states) */}
      {["approved", "completed", "resolved"].includes(run.status) && run.context?.technician && (
        <p className="text-xs text-green-600 bg-green-50 border border-green-100 rounded px-2 py-1">
          אושר על ידי {run.context.technician}
        </p>
      )}

      {/* Full analysis for completed/approved runs (collapsible) */}
      {["approved", "completed", "resolved"].includes(run.status) && run.analysis && (
        <details className="group">
          <summary className="text-xs text-gray-400 cursor-pointer select-none hover:text-gray-600 py-0.5">
            פרטי ניתוח AI
          </summary>
          <div className="mt-2 border-t border-gray-100 pt-2">
            <AnalysisDisplay analysis={run.analysis} onFillReply={onFillReply} />
          </div>
        </details>
      )}

      {/* Phase 2: execution result */}
      {run.context?.execution_result && (() => {
        const er = run.context.execution_result;
        const isOk          = er.success && er.executed;
        const isNoop        = er.success && !er.executed;
        const isCrisisBlocked = !er.success && !er.executed && ((er.message || "").includes("מצב חירום") || (er.message || "").includes("Crisis Mode"));
        const isFailed      = !er.success && !isCrisisBlocked;
        return (
          <div className={`rounded-md border px-2.5 py-2 text-xs ${
            isOk || isNoop    ? "border-green-200 bg-green-50 text-green-700"
            : isCrisisBlocked ? "border-red-200 bg-red-50 text-red-700"
            : isFailed        ? "border-amber-200 bg-amber-50 text-amber-700"
            :                   "border-gray-200 bg-gray-50 text-gray-500"
          }`}>
            <span className="font-semibold">
              {isOk ? "✓ בוצע: " : isNoop ? "✓ " : isCrisisBlocked ? "⚠️ חסום (מצב חירום): " : "✗ שגיאה: "}
            </span>
            {er.message}
          </div>
        );
      })()}

      {/* Fill email button for completed runs that have a reply draft */}
      {!isReview && !isFailed && onFillReply && run.analysis?.customer_reply_draft &&
       !(run.context?.execution_result?.action_type === "send_customer_message" && run.context?.execution_result?.executed) && (
        <div data-testid="run-card-draft-offer" className="pt-2 border-t border-dashed border-amber-200 space-y-1">
          {/* Before the offer it qualifies, so it is read first — the same order the collapsed row and
              `AnalysisDisplay` use. `degradedAnalysisLabel` returns null for any supported analysis,
              so a real draft offer is untouched. */}
          {cardDegradedLabel && (
            <span
              data-testid="analysis-degraded"
              className="inline-block text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
            >
              {cardDegradedLabel}
            </span>
          )}
          <div className="flex items-center justify-between">
            <p className="text-xs text-amber-600">✉ טיוטת תשובה ללקוח זמינה</p>
            <button
              type="button"
              onClick={() => onFillReply(run.analysis.customer_reply_draft)}
              className="text-xs text-violet-700 bg-violet-100 hover:bg-violet-200 border border-violet-300 px-2.5 py-1 rounded transition-colors"
            >
              מלא טופס מייל ↗
            </button>
          </div>
        </div>
      )}

      {/* Retry button for failed runs */}
      {isFailed && canAgent("agents_run") && (
        <div className="pt-1 border-t border-red-100">
          <button
            type="button"
            onClick={handleRetry}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            {busy ? "..." : "נסה שוב"}
          </button>
        </div>
      )}

      {/* Action buttons */}
      {isReview && !editMode && !rejectMode && canAgent("agents_approve") && (
        <div className="flex flex-wrap gap-2 pt-1 border-t border-amber-100">
          <button
            type="button"
            onClick={handleApprove}
            disabled={busy}
            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            {busy ? "..." : "אשר"}
          </button>
          <button
            type="button"
            onClick={() => setEditMode(true)}
            disabled={busy}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            ערוך ואשר
          </button>
          <button
            type="button"
            onClick={() => setRejectMode(true)}
            disabled={busy}
            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            דחה
          </button>
          <button
            type="button"
            onClick={handleStop}
            disabled={busy}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            עצור
          </button>
          {canAgent("agents_run") && (
            <button
              type="button"
              onClick={handleRequestMore}
              disabled={busy}
              className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            >
              נתח מחדש
            </button>
          )}
        </div>
      )}

      {/* Waiting-state action buttons: mark connection live or cancel */}
      {isWaitConn && canAgent("agents_approve") && (
        <div className="pt-1 border-t border-blue-100 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleConnectionActive}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            {busy ? "..." : "סמן חיבור פעיל"}
          </button>
          <button
            type="button"
            onClick={handleStop}
            disabled={busy}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
          >
            ביטול המתנה
          </button>
        </div>
      )}

      {/* Resume button for paused runs */}
      {isPaused && run.analysis && canAgent("agents_run") && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleResume}
              disabled={busy}
              className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            >
              {busy ? "..." : "המשך סקירה"}
            </button>
            <button
              type="button"
              onClick={handleRequestMore}
              disabled={busy}
              className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50"
            >
              ניתוח חדש
            </button>
          </div>
        </div>
      )}

      {/* Edit form */}
      {editMode && (
        <EditApproveForm
          run={run}
          onSubmit={handleFeedback}
          onCancel={() => setEditMode(false)}
          busy={busy}
        />
      )}

      {/* Reject form */}
      {rejectMode && (
        <RejectForm
          onSubmit={handleReject}
          onCancel={() => setRejectMode(false)}
          busy={busy}
        />
      )}

      {/* Local action error */}
      {localError && (
        <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{localError}</p>
      )}

      {/* Adaptive Knowledge Gate (MASTER_FLOW §4.1): the run's recorded mismatch and the two acts
          that put a run on the mismatch path and take it off again. Offered while the run is live,
          and revealed automatically when an approve was refused. It reads and writes nothing else:
          no execute, no approve, no scope. A viewer without agents_run sees no controls at all. */}
      {!_isTerminal && canAgent("agents_run") && (
        <div className="pt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={() => setGateVisible((v) => !v)}
            className="text-xs text-gray-500 hover:underline"
            data-testid="gate-toggle"
          >
            {gateVisible ? "הסתר שער הידע המסתגל" : "שער הידע המסתגל"}
          </button>
        </div>
      )}
      {gateVisible && <KnowledgeGatePanel runId={run.id} />}

      {/* Quality rating for terminal runs */}
      {/* rate_run needs agents_run (not view): a view-only user used to see thumbs that always failed silently */}
      {_isTerminal && canAgent("agents_run") && (
        <div className="pt-1 border-t border-gray-100 flex items-center gap-2">
          <span className="text-xs text-gray-400">האם הניתוח היה מועיל?</span>
          {rating ? (
            <span className="text-xs text-gray-500">{rating === "thumbs_up" ? "👍 תודה!" : "👎 תודה על המשוב"}</span>
          ) : (
            <>
              <button
                type="button"
                disabled={ratingBusy}
                onClick={() => handleRate("thumbs_up")}
                className="text-lg leading-none hover:scale-110 transition-transform disabled:opacity-50"
                title="מועיל"
              >👍</button>
              <button
                type="button"
                disabled={ratingBusy}
                onClick={() => handleRate("thumbs_down")}
                className="text-lg leading-none hover:scale-110 transition-transform disabled:opacity-50"
                title="לא מועיל"
              >👎</button>
            </>
          )}
        </div>
      )}

      {/* Actions log */}
      {run.actions?.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setActionsExpanded((v) => !v)}
            className="text-xs text-violet-600 hover:underline"
          >
            {actionsExpanded ? "הסתר פעולות" : `הצג ${run.actions.length} פעולות`}
          </button>
          {actionsExpanded && (
            <div className="mt-2 space-y-1.5 border-t pt-2">
              {run.actions.map((action) => (
                <div key={action.id} className="flex items-start gap-2 text-xs">
                  <span className="text-gray-400 font-mono shrink-0 w-4 text-center">
                    {action.sequence_no}
                  </span>
                  <span className={`shrink-0 border px-1.5 py-0.5 rounded text-xs font-medium ${RISK_BADGE[action.risk_level] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
                    {RISK_LABELS[action.risk_level] ?? action.risk_level}
                  </span>
                  <span className="text-gray-700 flex-1">{action.description}</span>
                  <span className={`shrink-0 font-medium ${OUTCOME_COLORS[action.outcome] ?? ""}`}>
                    {OUTCOME_LABELS[action.outcome] ?? action.outcome}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── AgentRunPanel ────────────────────────────────────────────────────────────

// Failed is just as terminal as approved/completed/resolved/rejected here: the backend's own AgentRun
// model sets finished_at for COMPLETED/FAILED/REJECTED/RESOLVED alike, and the ticket's
// pending_ai_run_status (shown on TicketsPage/Dashboard) changes the same way whether the run was
// approved, rejected, or auto-failed server-side (scheduler.py's stale-review/stuck-analyzing sweeps).
// "failed" was missing here despite this comment already naming it — the TicketsPage/tickets list
// cache was never invalidated on that one transition, so a ticket kept showing a live "awaiting
// review" AI badge for a run that had actually terminally failed, until the list's own 30s poll
// happened to catch up.
const TERMINAL_STATUSES = new Set(["approved", "rejected", "completed", "resolved", "failed"]);

export default function AgentRunPanel({ ticketId, ticket, onFillReply, defaultAgentId = "" }) {
  const { can: canAgent } = useAgentPermissions();
  const qc = useQueryClient();
  const [runs, setRuns]           = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [starting, setStarting]   = useState(false);
  const [error, setError]         = useState(null);
  const [agents, setAgents]       = useState([]);
  // a failed load is not "no diagnosis yet" / "no active agents": say so instead of offering to start / create one
  const [runsFailed, setRunsFailed]     = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [agentsFailed, setAgentsFailed] = useState(false);
  // The poller gave up after repeated failures. The run must stay explicitly unknown: the start
  // action stays disabled (a new run would be a duplicate if the old one is still alive), and a
  // read-only recheck — manual or once on reconnect — may recover tracking. It never auto-starts.
  const [trackingLost, setTrackingLost] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  // Restarts the polling effect after a successful recheck of a still-blocking run.
  const [pollNonce, setPollNonce] = useState(0);
  // null (not "") means "the operator has not chosen", and defaultAgentId -- the ticket's assigned
  // Digital Employee -- is used instead. An explicit "" is a real choice (the "ברירת מחדל" option)
  // and must override the default, so the sentinel is null rather than falsy.
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  // ESET-POC-001: device selection, required only for an agent whose backend-declared
  // requires_device_selection is true (currently: the ESET read-only connector).
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesFailed, setDevicesFailed] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  // Poll on BLOCKING_STATUSES, not just ACTIVE_STATUSES: awaiting_technician_review/awaiting_approval
  // (48h) and waiting_for_customer_connection/waiting_for_customer_answer (24h) can all be auto-failed
  // server-side by scheduler.py with no technician action, so the panel needs to notice those too
  // instead of showing a stale banner/controls until a reload.
  const needsPolling = activeRunId && activeRun && BLOCKING_STATUSES.has(activeRun.status);
  // Only agents that may work this ticket's customer (the backend refuses the rest).
  const selectableAgents = agentsForTicket(agents, ticket?.company_id ?? ticket?.company?.id);
  // A default the picker does not offer (a stale assignment, out-of-scope or disabled employee) is
  // dropped by usableAgentId, so the select can never show an employee the start would be refused for.
  const effectiveAgentId = usableAgentId(selectableAgents, selectedAgentId ?? defaultAgentId);
  const effectiveAgent = selectableAgents.find((a) => a.id === effectiveAgentId);
  const requiresDeviceSelection = !!effectiveAgent?.requires_device_selection;
  const companyIdForDevices = ticket?.company_id ?? ticket?.company?.id;

  const updateRun = useCallback((updated) => {
    setRuns((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    if (updated.id === activeRunId) {
      setActiveRun(updated);
      if (!BLOCKING_STATUSES.has(updated.status)) {
        setActiveRunId(null);
        setTrackingLost(false);
      }
    }
    if (TERMINAL_STATUSES.has(updated.status)) {
      qc.invalidateQueries({ queryKey: ["ticket", String(ticketId)] });
      qc.invalidateQueries({ queryKey: ["tickets"] });
    }
    // Approve/reject/stop/resume can all move a run out of the two statuses that count toward an
    // agent's pending_runs_count (see agents.py's _run_stats) -- refresh the three independent badge
    // caches that derive from it, matching AgentRunDetailPage.jsx's/AgentRunsDashboardPage.jsx's own
    // invalidate() for the same actions.
    qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
    qc.invalidateQueries({ queryKey: ["agents-dash"] });
    qc.invalidateQueries({ queryKey: ["agents"] });
    // This panel keeps its own run list in local state (fetched imperatively above), entirely
    // separate from TicketDetail.jsx's ["agentRuns", id] react-query cache that drives the page's
    // top AI-insight banner (status/confidence/quick approve-reject) and the "אבחון AI" tab badge
    // count. Without this, approving/rejecting/stopping/resuming a run from inside this panel left
    // that banner showing the old run status/action buttons -- including a now-stale quick-approve
    // button that would re-submit an approve/reject against an already-terminal run -- until the
    // banner's own refetchInterval happened to poll again.
    qc.invalidateQueries({ queryKey: ["agentRuns", String(ticketId)] });
  }, [activeRunId, qc, ticketId]);

  const addNewRun = useCallback((run) => {
    setActiveRunId(run.id);
    setActiveRun(run);
    setTrackingLost(false);
    setRuns((prev) => [run, ...prev.filter((r) => r.id !== run.id)]);
    // A retry / "request more analysis" creates a brand-new run that TicketDetail.jsx's own
    // ["agentRuns", id] cache (banner + tab badge) does not otherwise learn about.
    qc.invalidateQueries({ queryKey: ["agentRuns", String(ticketId)] });
  }, [qc, ticketId]);

  // Load previous runs and available agents on mount
  useEffect(() => {
    agentRunsApi.list(ticketId)
      .then((data) => {
        setRuns(data);
        setRunsFailed(false);
        const ongoing = data.find((r) => BLOCKING_STATUSES.has(r.status));
        if (ongoing) {
          setActiveRunId(ongoing.id);
          setActiveRun(ongoing);
          setTrackingLost(false);
        }
      })
      .catch(() => setRunsFailed(true))
      .finally(() => setLoading(false));
    agentsApi.list()
      .then((list) => {
        const enabled = list.filter((a) => a.is_enabled);
        setAgents(enabled);
        setAgentsFailed(false);
      })
      .catch(() => setAgentsFailed(true))
      .finally(() => setAgentsLoading(false));
  }, [ticketId]);

  // ESET-POC-001: bounded device list for the ticket's own company only, fetched only when the
  // effective agent actually requires a device selection — server-side re-validates this exact
  // company/device pairing again before any ESET call (never trust this list alone).
  useEffect(() => {
    setSelectedDeviceId("");
    if (!requiresDeviceSelection || !companyIdForDevices) {
      setDevices([]);
      setDevicesFailed(false);
      return;
    }
    setDevicesLoading(true);
    customerProfilesApi.devices(companyIdForDevices)
      .then((list) => {
        setDevices(list);
        setDevicesFailed(false);
      })
      .catch(() => setDevicesFailed(true))
      .finally(() => setDevicesLoading(false));
  }, [requiresDeviceSelection, companyIdForDevices]);

  // Poll active run every 3s while in an active status
  useEffect(() => {
    if (!needsPolling) return;
    let failures = 0;
    const interval = setInterval(async () => {
      try {
        const run = await agentRunsApi.get(activeRunId);
        failures = 0;
        setTrackingLost(false);
        updateRun(run);
        if (!BLOCKING_STATUSES.has(run.status)) {
          clearInterval(interval);
          // This final refresh used to be swallowed: a silent failure leaves the list
          // showing a stale row with no indication that the refresh did not land.
          agentRunsApi.list(ticketId)
            .then((data) => {
              setRuns(data);
              setRefreshFailed(false);
            })
            .catch(() => setRefreshFailed(true));
        }
      } catch {
        // One failed poll (network blip, brief 5xx) used to end tracking for good: the run kept going
        // on the server but this panel never saw it finish, so the review controls never appeared
        // until a reload. Give up only after several consecutive failures (e.g. the run is gone).
        failures += 1;
        if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
          clearInterval(interval);
          // Keep activeRunId/activeRun: the last known run is still the only run this panel knows
          // about, and the start action must remain blocked while its real state is unknown.
          setTrackingLost(true);
        }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeRunId, needsPolling, ticketId, updateRun, pollNonce]);

  const recheckActiveRun = useCallback(async () => {
    if (!activeRunId) return;
    setRechecking(true);
    try {
      const run = await agentRunsApi.get(activeRunId);
      updateRun(run);
      setTrackingLost(false);
      // Still blocking → restart the poller; terminal → updateRun cleared activeRunId, and the
      // effect below is a no-op because needsPolling is now false.
      setPollNonce((n) => n + 1);
    } catch {
      // The state remains unknown. This is a read-only recheck; it never starts or retries an action.
      setTrackingLost(true);
    } finally {
      setRechecking(false);
    }
  }, [activeRunId, updateRun]);

  // A reconnect is a read-only recovery: when tracking was lost, re-read the same run exactly
  // once. No mutation, start, approval or retry can be replayed by coming back online.
  useReconnectRefetch([trackingLost ? recheckActiveRun : null], {
    enabled: canAgent("agents_run"),
  });

  const startDiagnosis = async () => {
    if (requiresDeviceSelection && !selectedDeviceId) {
      setError("יש לבחור מכשיר לפני הפעלת בדיקת ESET");
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const extra = selectedDeviceId ? { eset_device_identifier: selectedDeviceId } : {};
      const run = await agentRunsApi.start(ticketId, effectiveAgentId || undefined, extra);
      addNewRun(run);
    } catch (err) {
      setError(err?.response?.data?.detail || "שגיאה בהפעלת האבחון");
    } finally {
      setStarting(false);
    }
  };

  // Merge live activeRun data into the runs list for display
  const displayRuns = runs.map((r) =>
    r.id === activeRunId && activeRun ? activeRun : r
  );

  const isCurrentlyRunning = activeRunId && activeRun && BLOCKING_STATUSES.has(activeRun.status);

  // Similar tickets: find closed tickets from same company with same AI category
  const _latestAnalysis = displayRuns.find((r) => r.analysis?.ai_category)?.analysis;
  // The category lives on the ticket (set by the classifier / poller); the run analysis never carries
  // an `ai_category`, so reading only that made this panel unreachable.
  const _similarCategory = ticket?.ai_category || _latestAnalysis?.ai_category;
  const _companyId = ticket?.company?.id;
  const { data: similarTickets = [] } = useQuery({
    queryKey: ["similar-tickets", _companyId, _similarCategory, ticketId],
    queryFn: () => ticketsApi.list({
      company_id: _companyId,
      ai_category: _similarCategory,
      status: "closed",
      limit: 5,
    }).then((r) => (Array.isArray(r) ? r : r.items ?? []).filter((t) => t.id !== ticketId)),
    enabled: !!(_companyId && _similarCategory),
    staleTime: 300_000,
  });

  return (
    <div className="rounded-lg border bg-gray-50/40 p-3 xl:p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-semibold text-gray-700">אבחון AI — Helpdesk Technician Agent</h2>
        <div className="flex items-center gap-2">
          {selectableAgents.length > 1 && (
            <select
              value={effectiveAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              disabled={starting || !!isCurrentlyRunning}
              className="border rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white disabled:opacity-50"
            >
              <option value="">ברירת מחדל</option>
              {selectableAgents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          {requiresDeviceSelection && (
            devicesFailed ? (
              <span role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
                לא ניתן לטעון את רשימת המכשירים
              </span>
            ) : (
              <select
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                disabled={starting || !!isCurrentlyRunning || devicesLoading}
                className="border rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white disabled:opacity-50"
              >
                <option value="">
                  {devicesLoading ? "טוען מכשירים..." : devices.length === 0 ? "אין מכשירים מוגדרים" : "בחר מכשיר"}
                </option>
                {devices.map((d) => (
                  <option key={d.name || d.ip} value={d.name || d.ip}>{d.name || d.ip}</option>
                ))}
              </select>
            )
          )}
          {!loading && !agentsLoading && agents.length === 0 && agentsFailed ? (
            <span role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              לא ניתן לטעון את רשימת הסוכנים
            </span>
          ) : !loading && !agentsLoading && agents.length === 0 ? (
            <Link
              to="/agents"
              className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg hover:bg-amber-100 transition-colors"
              title="אין סוכנים פעילים — צור סוכן ב-AI Technician Pilot ב-Settings"
            >
              אין סוכנים פעילים →
            </Link>
          ) : canAgent("agents_run") ? (
            <button
              type="button"
              onClick={startDiagnosis}
              disabled={starting || !!isCurrentlyRunning || (requiresDeviceSelection && !selectedDeviceId)}
              className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {starting
                ? "מתחיל..."
                : isCurrentlyRunning
                  ? (trackingLost
                      ? "מצב הריצה לא ידוע"
                      : REVIEW_STATUSES.has(activeRun?.status)
                        ? "ממתין לסקירה"
                        : ACTIVE_STATUSES.has(activeRun?.status)
                          ? "מנתח..."
                          : "פעיל")
                  : displayRuns.some((r) => ["completed", "approved", "resolved"].includes(r.status))
                    ? "נתח מחדש"
                    : "התחל אבחון AI"}
            </button>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        עובד AI · ביקורת טכנאי נדרשת לפני כל פעולה · Admin/Owner בלבד
      </p>

      {/* Global error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {trackingLost && (
        <div
          role="alert"
          data-testid="agent-run-tracking-lost"
          className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-center justify-between gap-3 flex-wrap"
        >
          <span>
            המעקב אחרי הריצה נקטע לאחר {MAX_CONSECUTIVE_POLL_FAILURES} כשלונות רענון רצופים. ייתכן
            שהריצה עדיין פעילה בשרת — מצבה אינו ידוע, ולא תתבצע התחלה כפולה.
          </span>
          <button
            type="button"
            onClick={recheckActiveRun}
            disabled={rechecking}
            className="border border-amber-300 bg-white text-amber-900 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-amber-100 disabled:opacity-50"
          >
            {rechecking ? "בודק…" : "בדוק שוב"}
          </button>
        </div>
      )}

      {loading && displayRuns.length === 0 && (
        <p className="text-sm text-gray-400 italic">טוען...</p>
      )}

      {!loading && displayRuns.length === 0 && runsFailed && (
        <p role="alert" className="text-sm text-red-600">
          לא ניתן לטעון את היסטוריית אבחוני ה-AI של הקריאה.
        </p>
      )}

      {!loading && displayRuns.length === 0 && !runsFailed && (
        <p className="text-sm text-gray-400 italic">
          לא בוצע אבחון AI לקריאה זו עדיין.
        </p>
      )}

      {refreshFailed && (
        <p role="alert" className="text-xs text-amber-700">
          רענון היסטוריית ההרצות לאחר סיום הריצה נכשל — ייתכן שהרשימה אינה מעודכנת.
        </p>
      )}

      <div className="space-y-3">
        {displayRuns.map((run, idx) => (
          <RunCard
            key={run.id}
            run={run}
            onRunUpdated={updateRun}
            onNewRun={addNewRun}
            onFillReply={onFillReply}
            isLatest={idx === 0}   // the list is NEWEST-first (backend orders created_at DESC, addNewRun prepends)
          />
        ))}
      </div>

      {similarTickets.length > 0 && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
            קריאות סגורות דומות ({_similarCategory})
          </p>
          <ul className="space-y-1">
            {similarTickets.map((t) => (
              <li key={t.id} className="flex items-start gap-2 text-xs">
                <span className="text-gray-300 shrink-0">#{t.ticket_number}</span>
                <Link
                  to={`/tickets/${t.id}`}
                  className="text-gray-600 hover:text-brand-600 hover:underline line-clamp-1 flex-1"
                  title={t.title}
                >
                  {t.title}
                </Link>
                {t.resolution_note && (
                  <span className="text-gray-400 line-clamp-1 max-w-[120px] shrink-0" title={t.resolution_note}>
                    → {t.resolution_note}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
